import { useEffect, type ReactNode } from 'react';
import { FONT_MONO, v } from '../constants';
import type { Difficulty } from '../types';

// ─────────────────────────────────────────────────────────────────────
// Modals
// ─────────────────────────────────────────────────────────────────────
export function Modal({ onClose, children, ariaLabel }: { onClose: () => void; children: ReactNode; ariaLabel?: string }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="hm-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label={ariaLabel}>
      <div className="hm-modal" onClick={e => e.stopPropagation()}>
        <button type="button" className="hm-modal-close" onClick={onClose} aria-label="Close">×</button>
        {children}
      </div>
    </div>
  );
}

// Two-button menu shown when the user hits "How to play" / "Help" / the
// mobile `?` icon. Lets them either replay the interactive tutorial or
// open the legacy text rundown (HowToPlayModal) — same content as before
// the tutorial existed, kept around for users who'd rather skim than
// play. The very-first-visit path skips this menu and opens the
// tutorial directly (see App.tsx).
export function HelpMenuModal({
  onReplayTutorial,
  onShowText,
  onClose,
}: {
  onReplayTutorial: () => void;
  onShowText: () => void;
  onClose: () => void;
}) {
  return (
    <Modal onClose={onClose} ariaLabel="Help">
      <h2>Help</h2>
      <p>How would you like a refresher?</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
        <button
          type="button"
          className="hm-action"
          style={{
            padding: '14px 16px',
            justifyContent: 'space-between',
            background: v('ink'),
            color: v('paper'),
            border: 'none',
          }}
          onClick={onReplayTutorial}
        >
          <span style={{ fontWeight: 600, fontSize: 14 }}>Replay tutorial</span>
          <span style={{ fontSize: 11, opacity: 0.75 }}>interactive · 1 min</span>
        </button>
        <button
          type="button"
          className="hm-action"
          style={{ padding: '14px 16px', justifyContent: 'space-between' }}
          onClick={onShowText}
        >
          <span style={{ fontWeight: 600, fontSize: 14 }}>Show how-to-play text</span>
          <span style={{ fontSize: 11, color: v('inkMute') }}>full reference</span>
        </button>
      </div>
    </Modal>
  );
}

export function HowToPlayModal({ onClose }: { onClose: () => void }) {
  return (
    <Modal onClose={onClose} ariaLabel="How to play">
      <h2>How to play</h2>
      <p>
        Place every tile from the bag onto the hex board so that each line —
        column, NW→SE diagonal, NE→SW diagonal — satisfies the small mark at
        its end.
      </p>

      <h3>Placing tiles</h3>
      <p>
        Tap a tile in the bag to pick it up, then tap an open cell on the board
        to drop it. Tap a tile you've placed (not the locked ones) to send it
        back to the bag.
      </p>

      <h3>Locked tiles</h3>
      <p>
        Hexes that already have a number with a small lock are fixed — they're
        part of the puzzle.
      </p>

      <h3>Restricted cells</h3>
      <p>
        Dashed cells require a specific kind of number:
      </p>
      <ul style={{ fontFamily: FONT_MONO, fontSize: 12 }}>
        <li><strong>Even</strong> — even</li>
        <li><strong>Odd</strong> — odd</li>
        <li><strong>+</strong> — positive (≥ 1)</li>
        <li><strong>−</strong> — negative (≤ −1)</li>
        <li><strong>p</strong> — prime (2, 3, 5, 7…)</li>
        <li><strong>|x|≥5</strong> — magnitude at least 5</li>
        <li><strong>|x|≤4</strong> — magnitude at most 4</li>
      </ul>

      <h3>Modifier hexes</h3>
      <p>
        Cells with a label like <strong>*2</strong>, <strong>+3</strong>,
        <strong>÷2</strong>, or <strong>²</strong> rewrite the tile you place
        there for every line passing through. The original tile value is shown
        small in the corner so you can read both at a glance.
      </p>

      <h3>The marks</h3>
      <ul style={{ fontFamily: FONT_MONO, fontSize: 12 }}>
        <li><strong>Σ&nbsp;14</strong> — line sum equals 14</li>
        <li><strong>×&nbsp;24</strong> — line product equals 24</li>
        <li><strong>Even</strong> — every value on the line is even</li>
        <li><strong>Positive</strong> — no negatives on the line</li>
        <li><strong>All Negative</strong> — every value is negative</li>
        <li><strong>3 Negatives</strong> — exactly three negatives on the line</li>
      </ul>

      <p style={{ marginTop: 18 }}>
        Marks turn <span style={{ color: v('ok') }}>green</span> when satisfied
        and <span style={{ color: v('bad') }}>red</span> when broken. Solve by
        making them all green at once.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 18, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, color: v('inkMute') }}>
          <a href="/privacy" style={{ color: v('inkSoft') }}>Privacy</a>
          {' · '}
          <a href="/terms" style={{ color: v('inkSoft') }}>Terms</a>
        </div>
        <button type="button" className="hm-primary" onClick={onClose}>Got it</button>
      </div>
    </Modal>
  );
}

export function formatTime(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

interface WinModalProps {
  time: number;
  streak: number;
  difficulty: Difficulty;
  onClose: () => void;
  onPlayAgain: () => void;
  onChangeDifficulty: () => void;
  /// Optional — when provided, the win modal surfaces a "See today's
  /// leaderboard" button. App.tsx wires this to open the
  /// DailyLeaderboardModal for the current (date, difficulty).
  onShowLeaderboard?: () => void;
}
export function WinModal({ time, streak, difficulty, onClose, onPlayAgain, onChangeDifficulty, onShowLeaderboard }: WinModalProps) {
  return (
    <Modal onClose={onClose} ariaLabel="Puzzle solved">
      <h2>Solved.</h2>
      <p style={{ fontFamily: FONT_MONO, fontSize: 13, color: v('inkMute') }}>
        {difficulty} · all marks green · all tiles placed
      </p>
      <div className="hm-win-stats">
        <div className="hm-win-stat">
          <div className="hm-win-stat-label">Time</div>
          <div className="hm-win-stat-value">{formatTime(time)}</div>
        </div>
        <div className="hm-win-stat">
          <div className="hm-win-stat-label">Streak</div>
          <div className="hm-win-stat-value">{streak}</div>
        </div>
        <div className="hm-win-stat">
          <div className="hm-win-stat-label">Difficulty</div>
          <div className="hm-win-stat-value" style={{ fontSize: 14 }}>{difficulty}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {onShowLeaderboard && (
          <button type="button" className="hm-action" onClick={onShowLeaderboard}>
            See today's leaderboard
          </button>
        )}
        <button type="button" className="hm-action" onClick={onChangeDifficulty}>
          Change difficulty
        </button>
        <button type="button" className="hm-primary" onClick={onPlayAgain}>
          Play again
        </button>
      </div>
    </Modal>
  );
}
